import {
  AsYouType,
  getCountries,
  getCountryCallingCode,
  isSupportedCountry,
  Metadata,
  parsePhoneNumberFromString
} from "libphonenumber-js"

const PLANS = new Metadata()
const mainCountry = (callingCode) => PLANS.getCountryCodesForCallingCode?.(callingCode)?.[0]

const callingCodeOf = (iso) => (iso && isSupportedCountry(iso) ? getCountryCallingCode(iso) : null)

// Reads whatever the user typed or pasted. A "+" or "00" number carries its own
// country; anything else is read in the picked country (so a leading 0 is the
// national trunk prefix, not a digit to keep). Nothing is ever cut.
// Returns { iso, text, e164, valid }. Save e164 only when valid is true.
export const readPhone = (text, iso) => {
  let input = (text || "").trim()
  if (input.startsWith("00")) input = `+${input.slice(2)}`
  const intl = input.startsWith("+")
  const pickedIso = isSupportedCountry(iso || "") ? iso : undefined
  // A "+" number carries its own country code, so read it without the picked country.
  const parsed = parsePhoneNumberFromString(input, intl ? undefined : pickedIso)
  const valid = !!parsed?.isValid()
  let country = iso
  if (intl) {
    if (parsed?.country) country = parsed.country
    else {
      const typer = new AsYouType()
      typer.input(input)
      const code = typer.getCallingCode()
      if (code && code !== callingCodeOf(iso)) country = mainCountry(code) || iso
    }
  }
  // Keep the "+" while the number is incomplete. Switch to the national format once it is complete.
  const display =
    intl && valid && parsed.country
      ? parsed.formatNational()
      : new AsYouType(intl ? undefined : pickedIso).input(input)
  return { iso: country, text: display, e164: parsed?.number || null, valid }
}

// A number saved on the backend (E.164) -> what the input shows.
export const parseStoredPhone = (stored, fallbackIso) => {
  const parsed = stored ? parsePhoneNumberFromString(String(stored).trim()) : null
  if (!parsed) return { iso: fallbackIso, text: stored || "", e164: null, valid: false }
  return {
    iso: parsed.country || fallbackIso,
    text: parsed.country ? parsed.formatNational() : parsed.formatInternational(),
    e164: parsed.number,
    valid: parsed.isValid()
  }
}

const LANG_TO_COUNTRY = {
  sv: "SE",
  de: "DE",
  fr: "FR",
  es: "ES",
  it: "IT",
  nl: "NL",
  pt: "PT",
  da: "DK",
  nb: "NO",
  fi: "FI",
  pl: "PL",
  en: "US"
}

// Country preset from the browser: "sv-SE" -> SE; a bare "sv" -> SE via the table; else US.
export const browserCountry = (knownIsoCodes) => {
  const known = knownIsoCodes?.length ? knownIsoCodes : getCountries()
  const langs = navigator.languages?.length ? navigator.languages : [navigator.language || "en-US"]
  for (const l of langs) {
    const region = l.split("-")[1]?.toUpperCase()
    if (region && known.includes(region)) return region
  }
  const base = (langs[0] || "en").split("-")[0].toLowerCase()
  return LANG_TO_COUNTRY[base] || "US"
}

// The countries list (/user/countries/) entry for an ISO code, in the shape the
// phone value carries: { code, countryCode, name, id }.
export const countryFields = (countries, iso) => {
  const match = (countries || []).find(
    (c) => (c.country_code || "").toUpperCase() === String(iso || "").toUpperCase()
  )
  if (match) {
    return { code: match.code, countryCode: match.country_code, name: match.name, id: match.id }
  }
  const code = callingCodeOf(iso)
  return iso ? { code: code || "", countryCode: iso } : {}
}

// Builds the PhoneInput value from a readPhone() result.
export const toPhoneValue = (countries, { iso, text, e164, valid }) => ({
  ...countryFields(countries, iso),
  number: text,
  e164,
  valid
})

// The phone value a form starts with: the saved number, or an empty field on the
// browser's country. `storedIso` is the saved number's country, for a number
// libphonenumber can't place.
export const initialPhoneValue = (countries, stored, storedIso) => {
  const fallbackIso = storedIso || browserCountry((countries || []).map((c) => c.country_code))
  if (!stored)
    return toPhoneValue(countries, { iso: fallbackIso, text: "", e164: null, valid: false })
  return toPhoneValue(countries, parseStoredPhone(stored, fallbackIso))
}
